import { isDefined } from "@clipboard-health/util-ts";
import { type RecentColleague } from "@src/appV2/Agents/api/useGetRecentColleagues";
import type { BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import constate from "constate";
import { useEffect, useState } from "react";
import { generatePath, useHistory, useLocation } from "react-router-dom";

import { type ShiftDiscoverySearchMode } from "../../OpenShifts/ShiftAction/api/useClaimShift";
import { type ComputedArrivalData } from "../ArrivalTime/computeArrivalData";
import { type ArrivalTimePoints } from "../ArrivalTime/useArrivalTimePoints";
import { useCredentialRequirementConfiguration } from "../Documents/useCredentialRequirementConfiguration";
import { getBlockingRequirementsContext } from "../Shift/Bookability/Documents/getBlockingRequirementsContext";
import { type BlockingRequirementsContext } from "../Shift/Bookability/Documents/types";
import { type BundleShiftPay, type ShiftBundle } from "../Shift/Bundle/types";
import { type ShiftSlotCoalesced } from "../Shift/Open/useGetShiftsSlotV2";
import type { ShiftBoost } from "../Shift/ShiftBoost/api/schema";
import { type ModalShiftData } from "../Shift/types";
import { useGetWorkplacesById } from "../Workplace/useGetWorkplacesById";
import { type WorkerPublicProfile } from "../WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { getShiftModalRouteParams } from "./getShiftModalRouteParams";
import { useGetModalShiftData } from "./useGetModalShiftData";

interface UseShiftModalsDataProps {
  /**
   * Base path used for navigation when opening/closing facility modals.
   * This path is used to:
   * 1. Determine where to navigate when closing modals
   * 2. Support rendering the same modals from different root views (map/list)
   */
  baseNavigationPath: string;

  /**
   * Search mode used by underlying components to identify which mode was used to open shift modals.
   */
  searchMode: ShiftDiscoverySearchMode;
}

/**
 * Used to pre-populate shift modals with data so that the modals can be rendered immediately.
 * The data is refreshed by the useGetModalShiftData when the modals are opened.
 */
interface InitialShiftModalsData {
  shift?: ModalShiftData;
  availableShiftBoost?: ShiftBoost;
  appliedShiftBoost?: ShiftBoost;
  bookabilityStatus?: BookabilityStatusItem;
  resolvedShiftQualification?: string;
  hasHoursRestrictionConflict?: boolean;
  blockingRequirementsContext?: BlockingRequirementsContext;
  shiftSlots?: ShiftSlotCoalesced;
  friendsMap?: Map<string, WorkerPublicProfile>;
  recentColleaguesMap?: Map<string, RecentColleague>;
  outboundFriendRequestsSet?: Set<string>;
  isIndependentContractorBadgesRequired?: boolean;
  computedArrivalData?: ComputedArrivalData;
  arrivalTimePoints?: ArrivalTimePoints;
  bundle?: ShiftBundle;
  /** Pre-computed pay for each shift in the bundle, keyed by shift ID */
  bundleShiftPays?: Map<string, BundleShiftPay>;
  /** Results from booking bundle shifts, set after a bundle booking attempt */
  bundleBookingResults?: { bookedShiftIds: string[]; failedShiftIds: string[] };
}

const InitialShiftModalsDataValue: InitialShiftModalsData = {
  shift: undefined,
  availableShiftBoost: undefined,
  appliedShiftBoost: undefined,
  bookabilityStatus: undefined,
  hasHoursRestrictionConflict: undefined,
  blockingRequirementsContext: undefined,
  outboundFriendRequestsSet: undefined,
  isIndependentContractorBadgesRequired: false,
  computedArrivalData: undefined,
  arrivalTimePoints: undefined,
  bundle: undefined,
  bundleShiftPays: undefined,
  bundleBookingResults: undefined,
};

/**
 * This context manages the state for shift modals with these key features:
 * 1. Path generation - Handles both direct and nested modal paths
 * 2. Base path override - Supports rendering on top of workplace modals
 *
 * Modal Hierarchy:
 * - Shift modals can appear independently or on top of workplace modals
 * - When appearing on top of workplace modals, baseNavigationPathOverride is used
 * - This allows the workplace modal to stay mounted while shift modals animate in/out
 *
 * Usage:
 * - Direct modal: uses baseNavigationPath
 * - Nested modal: uses baseNavigationPath + baseNavigationPathOverride
 */
function useShiftModalsData(props: UseShiftModalsDataProps) {
  const { baseNavigationPath, searchMode } = props;

  /**
   * When shift modals need to open on top of an existing modal (e.g. workplace open shifts modal),
   * we override the base navigation path. This ensures the new modal is rendered in addition to
   * the currently visible base modal, rather than replacing it.
   */
  const [baseNavigationPathOverride, setBaseNavigationPathOverride] = useState<string>();

  const history = useHistory();
  const worker = useDefinedWorker();
  const location = useLocation();

  const navigateToModal = <S extends string>(
    path: S,
    options?: {
      pathParams?: Parameters<typeof generatePath<S>>[1];
      searchParams?: Record<string, string>;
      clearExistingSearchParams?: boolean;
      forceReplacePath?: boolean;
    }
  ) => {
    const searchParams = new URLSearchParams(
      options?.clearExistingSearchParams === true ? undefined : location.search
    );

    if (options?.searchParams) {
      Object.entries(options.searchParams).forEach(([key, value]) => {
        searchParams.set(key, value);
      });
    }

    const modalPath = generatePath(
      `${baseNavigationPath}${
        baseNavigationPathOverride ? `/${baseNavigationPathOverride}` : ""
      }/${path}`,
      options?.pathParams as unknown as
        | Parameters<typeof generatePath<`${string}/${S}`>>[1]
        | undefined
    );

    const finalPath = searchParams.size > 0 ? `${modalPath}?${searchParams.toString()}` : modalPath;

    if (isDefined(baseNavigationPathOverride) || options?.forceReplacePath) {
      history.replace(finalPath);
    } else {
      history.push(finalPath);
    }
  };

  const { pathname } = useLocation();

  const isNestedModalContext = isDefined(baseNavigationPathOverride);
  const modalRouteParams = getShiftModalRouteParams(
    isNestedModalContext
      ? `${baseNavigationPath}/${baseNavigationPathOverride}`
      : baseNavigationPath,
    pathname
  );

  const shiftId = modalRouteParams?.shiftId;

  const [initialShiftModalsData, setInitialShiftModalsData] = useState<InitialShiftModalsData>(
    InitialShiftModalsDataValue
  );

  const initialDataIsStale =
    isDefined(shiftId) &&
    isDefined(initialShiftModalsData.shift) &&
    initialShiftModalsData.shift.id !== shiftId;

  useEffect(() => {
    if (initialDataIsStale) {
      setInitialShiftModalsData(InitialShiftModalsDataValue);
    }
  }, [initialDataIsStale, setInitialShiftModalsData]);

  const { data, isLoading, shiftFailedLoading, shiftFailedReasons, stateTransitionTimings } =
    useGetModalShiftData({
      shiftId,
    });

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const shift = data?.shift ?? initialShiftModalsData?.shift;
  const availableShiftBoost =
    data?.availableShiftBoost ?? initialShiftModalsData?.availableShiftBoost;
  const appliedShiftBoost = data?.appliedShiftBoost ?? initialShiftModalsData?.appliedShiftBoost;
  const bookabilityStatus = data?.bookabilityStatus ?? initialShiftModalsData?.bookabilityStatus;
  const resolvedShiftQualification = data?.resolvedShiftQualification;
  const shiftSlots = data?.shiftSlots ?? initialShiftModalsData?.shiftSlots;
  const friendsMap = data?.friendsMap ?? initialShiftModalsData?.friendsMap;
  const recentColleaguesMap =
    data?.recentColleaguesMap ?? initialShiftModalsData?.recentColleaguesMap;
  const hasHoursRestrictionConflict =
    data?.hasHoursRestrictionConflict ?? initialShiftModalsData?.hasHoursRestrictionConflict;
  const outboundFriendRequestsSet =
    data?.outboundFriendRequestsSet ?? initialShiftModalsData?.outboundFriendRequestsSet;
  const computedArrivalData =
    data?.computedArrivalData ?? initialShiftModalsData?.computedArrivalData;
  const arrivalTimePoints = data?.arrivalTimePoints ?? initialShiftModalsData?.arrivalTimePoints;
  const bundle = initialShiftModalsData?.bundle;
  const bundleShiftPays = initialShiftModalsData?.bundleShiftPays;
  const bundleBookingResults = initialShiftModalsData?.bundleBookingResults;

  const blockingRequirementsContext = isDefined(data?.blockingRequirements)
    ? getBlockingRequirementsContext(data.blockingRequirements, credentialRequirementConfiguration)
    : initialShiftModalsData?.blockingRequirementsContext;

  const workplaceId = shift?.workplace.id ?? initialShiftModalsData?.shift?.workplace.id;
  const { data: workplaces } = useGetWorkplacesById(
    { workplaceIds: workplaceId ? [workplaceId] : [] },
    { enabled: isDefined(workplaceId) }
  );

  const isIndependentContractorBadgesRequired =
    workplaces?.pages.at(0)?.data.at(0)?.attributes.independentContractorBadge.enabled ?? false;

  return {
    baseNavigationPath,
    baseNavigationPathOverride,
    blockingRequirementsContext,
    bookabilityStatus,
    availableShiftBoost,
    appliedShiftBoost,
    hasHoursRestrictionConflict,
    hasMarkedInterest: shift?.attributes.isInterested,
    isLoadingShiftModalsData: isLoading,
    modalRouteParams,
    navigateToModal,
    resolvedShiftQualification,
    searchMode,
    setBaseNavigationPathOverride,
    setInitialShiftModalsData,
    shift,
    shiftFailedLoading,
    shiftFailedReasons,
    shiftId,
    shiftSlots,
    friendsMap,
    recentColleaguesMap,
    outboundFriendRequestsSet,
    worker,
    computedArrivalData,
    isIndependentContractorBadgesRequired,
    arrivalTimePoints,
    bundle,
    bundleShiftPays,
    bundleBookingResults,
    stateTransitionTimings,
  };
}

export type NavigateToModalFunction = ReturnType<typeof useShiftModalsData>["navigateToModal"];

export const [ShiftModalsDataProvider, useShiftModalsDataContext] = constate(useShiftModalsData);
